import { useState } from "react";
import { motion } from "framer-motion";
import {
  Check,
  ShieldCheck,
  ArrowDownRight,
  Instagram,
  Menu,
  X,
  Share2,
  Link as LinkIcon,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { createCheckoutSession } from "@/lib/api";

/* ===========================================================================
 * Light editorial variant of the landing page.
 * Palette: warm cream (#FAF6EE) + ink (#1A1814) + copper accent (#B05632).
 * Typography: Spectral throughout (weights 300–700, italic for emphasis).
 * =========================================================================== */

const C = {
  bg: "#F7F7F5",
  bgSoft: "#FFFFFF",
  bgWarm: "#F0F0EE",
  ink: "#0F0F0F",
  inkSoft: "#4A4A4A",
  inkMuted: "#8A8A8A",
  accent: "#2D5544",
  accentHover: "#3A6B57",
  accentDeep: "#1F3D30",
  coral: "#B85042",
  border: "#E5E5E5",
  borderSoft: "#F0F0F0",
};

const DISPLAY = { fontFamily: "'Lato', sans-serif" };
const BODY = { fontFamily: "'Lato', sans-serif" };
const SPECTRAL = DISPLAY;

const trackGA = (eventName, params = {}) => {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", eventName, params);
    }
  } catch (e) {
    // GA not loaded — silent fail
  }
};

const useBuy = () => {
  const [loading, setLoading] = useState(false);
  const handle = async (email = null) => {
    try {
      setLoading(true);
      trackGA("begin_checkout", {
        currency: "EUR",
        value: 47,
        items: [{ item_id: "dnots_launch", item_name: "The Dark Night of the Soul Guide", price: 47, quantity: 1 }],
      });
      const { url } = await createCheckoutSession({ email });
      window.location.href = url;
    } catch (e) {
      console.error(e);
      toast.error("Could not start checkout. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return { loading, handle };
};

/* -------------------------------------------------------- Navbar -------- */
function NavLight() {
  const [open, setOpen] = useState(false);

  const items = [
    { l: "The Guide", h: "#guide" },
    { l: "What's Inside", h: "#inside" },
    { l: "My Story", h: "#story" },
    { l: "FAQ", h: "#faq" },
  ];

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50"
      style={{
        background: C.bg,
        borderBottom: `1px solid ${C.border}`,
      }}
      data-testid="light-navbar"
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 h-16 flex items-center justify-between">
        <a
          href="#top"
          className="text-xl tracking-tight"
          style={{ ...SPECTRAL, color: C.ink }}
          data-testid="light-brand-logo"
        >
          Robin{" "}
          <span style={{ fontStyle: "italic", color: C.accent }}>
            Weyersberg
          </span>
        </a>

        <div className="hidden md:flex items-center gap-10">
          {items.map((it) => (
            <a
              key={it.h}
              href={it.h}
              className="transition-colors duration-300"
              style={{
                ...SPECTRAL,
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                color: C.inkSoft,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = C.ink)}
              onMouseLeave={(e) => (e.currentTarget.style.color = C.inkSoft)}
            >
              {it.l}
            </a>
          ))}
          <button
            onClick={() => {
              trackGA("cta_click", { cta_location: "nav_desktop", cta_label: "Buy · 47€" });
              document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
            }}
            data-testid="light-nav-buy-button"
            className="transition-colors duration-300"
            style={{
              ...BODY,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "0.25em",
              textTransform: "uppercase",
              background: C.accent,
              color: C.bg,
              padding: "10px 20px",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = C.accentHover)}
            onMouseLeave={(e) => (e.currentTarget.style.background = C.accent)}
          >
            Buy · 47€
          </button>
        </div>

        <button
          onClick={() => setOpen((o) => !o)}
          className="md:hidden"
          style={{ color: C.ink }}
          data-testid="light-nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {open && (
        <div
          className="md:hidden"
          style={{
            borderTop: `1px solid ${C.border}`,
            background: C.bg,
          }}
          data-testid="light-mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {items.map((it) => (
              <a
                key={it.h}
                href={it.h}
                onClick={() => setOpen(false)}
                style={{
                  ...SPECTRAL,
                  fontSize: 12,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: C.inkSoft,
                }}
              >
                {it.l}
              </a>
            ))}
            <button
              onClick={() => {
                trackGA("cta_click", { cta_location: "nav_mobile", cta_label: "Buy · 47€" });
                setOpen(false);
                setTimeout(() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" }), 100);
              }}
              data-testid="light-nav-buy-button-mobile"
              style={{
                ...BODY,
                fontSize: 12,
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                background: C.accent,
                color: C.bg,
                padding: "12px 20px",
              }}
            >
              Buy · 47€
            </button>
            <Link
              to="/"
              style={{
                ...SPECTRAL,
                fontSize: 11,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: C.inkMuted,
              }}
            >
              ← View Dark Version
            </Link>
          </div>
        </div>
      )}
    </motion.header>
  );
}

/* -------------------------------------------------------- Overline ------ */
const Overline = ({ children, testId }) => (
  <div
    className="flex items-center gap-4"
    data-testid={testId}
    style={{ marginBottom: 32 }}
  >
    <span style={{ height: 1, width: 40, background: C.coral }} />
    <span
      style={{
        ...BODY,
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: "0.3em",
        textTransform: "uppercase",
        color: C.coral,
      }}
    >
      {children}
    </span>
  </div>
);

/* -------------------------------------------------------- Header Banner */
function HeaderBanner() {
  return (
    <section
      className="relative w-full overflow-hidden"
      data-testid="light-header-banner"
      style={{
        marginTop: 64, // sit below the fixed 64px navbar
        background: C.bgWarm,
        height: "clamp(360px, 62vh, 640px)",
      }}
    >
      <img
        src="/hero-portrait.jpg"
        alt="A face crowned by orange poppies — the old self dissolving so the real you can emerge"
        className="w-full h-full object-cover"
        style={{ objectPosition: "center 45%" }}
        loading="eager"
        fetchpriority="high"
      />
      {/* Soft fade at the bottom into the page background */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 pointer-events-none"
        style={{
          height: "30%",
          background: `linear-gradient(to bottom, transparent, ${C.bg})`,
        }}
      />
    </section>
  );
}

/* -------------------------------------------------------- Hero ---------- */
function HeroLight() {
  return (
    <section
      id="top"
      className="relative flex flex-col justify-end overflow-hidden"
      data-testid="light-hero-section"
      style={{ background: C.bg, minHeight: "70vh", paddingTop: 80 }}
    >
      {/* Subtle ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full"
        style={{
          background:
            "radial-gradient(circle at center, rgba(45,85,68,0.07) 0%, rgba(45,85,68,0.02) 30%, transparent 60%)",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-12 lg:px-16 pb-20 md:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-4 mb-12"
        >
          <span style={{ height: 1, width: 40, background: C.coral }} />
          <span
            style={{
              ...BODY,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "0.35em",
              textTransform: "uppercase",
              color: C.coral,
            }}
          >
            The Dark Night of the Soul
          </span>
          <span
            className="hidden md:inline"
            style={{
              ...SPECTRAL,
              fontSize: 11,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: C.inkMuted,
            }}
          >
            · A 6-Phase Transformation Guide
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 1.4,
            delay: 0.2,
            ease: [0.22, 1, 0.36, 1],
          }}
          data-testid="light-hero-headline"
          className="max-w-6xl"
          style={{
            ...SPECTRAL,
            fontWeight: 700,
            color: C.ink,
            fontSize: "clamp(48px, 9vw, 130px)",
            lineHeight: 0.96,
            letterSpacing: "-0.02em",
          }}
        >
          Walk through the
          <br />
          <span style={{ fontStyle: "italic", color: C.accent }}>darkness</span>
          <span style={{ color: C.inkMuted, margin: "0 0.2em" }}>—</span>
          <br />
          come back as
          <br />
          who you truly are.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.6 }}
          data-testid="light-hero-subheadline"
          className="max-w-2xl"
          style={{
            ...BODY,
            fontSize: 20,
            fontWeight: 400,
            lineHeight: 1.6,
            color: C.ink,
            marginTop: 48,
          }}
        >
          For the ones who feel hollow on the inside while functioning on the
          outside. A six-phase, neuroscience-rooted descent that ends the
          borrowed life — and lets the real you finally emerge.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.9 }}
          className="mt-14 flex flex-col md:flex-row md:items-center gap-8"
        >
          <a
            href="#pricing"
            data-testid="light-hero-primary-cta"
            className="inline-flex items-center gap-4 transition-colors duration-300"
            style={{
              background: C.accent,
              color: C.bg,
              padding: "16px 32px",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = C.accentHover)
            }
            onMouseLeave={(e) => (e.currentTarget.style.background = C.accent)}
          >
            <span
              style={{
                ...SPECTRAL,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
              }}
            >
              Begin the Descent
            </span>
            <span
              style={{
                ...SPECTRAL,
                fontSize: 11,
                letterSpacing: "0.2em",
                opacity: 0.7,
              }}
            >
              · 47€
            </span>
          </a>

          <div
            className="flex items-center gap-4"
            style={{
              ...SPECTRAL,
              fontSize: 11,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: C.inkMuted,
            }}
          >
            <span style={{ textDecoration: "line-through" }}>97€</span>
            <span style={{ color: C.accent }}>Launch Price · Limited</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.5, delay: 1.4 }}
          className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-8"
          style={{ borderTop: `1px solid ${C.border}`, paddingTop: 40 }}
        >
          {[
            { k: "154K", v: "Followers" },
            { k: "6", v: "Phases" },
            { k: "35", v: "Deep Questions" },
            { k: "6", v: "Practical Tools" },
          ].map((s) => (
            <div key={s.v}>
              <div
                style={{
                  ...SPECTRAL,
                  fontSize: 40,
                  fontWeight: 400,
                  color: C.ink,
                  letterSpacing: "-0.02em",
                }}
              >
                {s.k}
              </div>
              <div
                style={{
                  ...SPECTRAL,
                  fontSize: 10,
                  letterSpacing: "0.25em",
                  textTransform: "uppercase",
                  color: C.inkMuted,
                  marginTop: 4,
                }}
              >
                {s.v}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- Quote Break -- */
function QuoteLight({ quote, italic, slug, testId }) {
  const [copied, setCopied] = useState(false);
  const SITE_URL =
    process.env.REACT_APP_SITE_URL ||
    (typeof window !== "undefined" ? window.location.origin : "");
  const shareUrl = slug ? `${SITE_URL}/api/share/${slug}` : null;

  const onShare = async () => {
    if (!shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "The Dark Night of the Soul",
          text: `"${quote} ${italic || ""}"`,
          url: shareUrl,
        });
        return;
      } catch {
        /* fall through */
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <section
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-16 overflow-hidden"
      data-testid={testId}
      style={{
        background: C.bgSoft,
        borderTop: `1px solid ${C.border}`,
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      <div className="max-w-6xl mx-auto relative">
        <motion.blockquote
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          style={{
            ...SPECTRAL,
            fontWeight: 700,
            color: C.ink,
            fontSize: "clamp(32px, 5.5vw, 72px)",
            lineHeight: 1.05,
            letterSpacing: "-0.015em",
          }}
        >
          <span
            style={{
              color: C.accent,
              fontSize: "1.4em",
              lineHeight: 0,
              marginRight: 8,
              verticalAlign: "top",
            }}
          >
            &ldquo;
          </span>
          {quote}
          {italic && (
            <span style={{ fontStyle: "italic", color: C.accent }}>
              {" "}
              {italic}
            </span>
          )}
          <span
            style={{ color: C.accent, fontSize: "1.4em", lineHeight: 0 }}
          >
            &rdquo;
          </span>
        </motion.blockquote>

        {slug && (
          <div
            className="mt-10 flex flex-wrap items-center justify-between gap-4"
            data-testid={`light-share-row-${slug}`}
          >
            <a
              href={shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 transition-colors"
              style={{
                ...SPECTRAL,
                fontSize: 11,
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                color: C.inkMuted,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = C.ink)}
              onMouseLeave={(e) => (e.currentTarget.style.color = C.inkMuted)}
            >
              <LinkIcon size={11} />
              Preview share page
            </a>

            <button
              onClick={onShare}
              data-testid={`light-share-quote-${slug}`}
              className="inline-flex items-center gap-3 transition-colors"
              style={{
                ...SPECTRAL,
                fontSize: 10,
                fontWeight: 500,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                border: `1px solid ${C.border}`,
                color: C.inkSoft,
                padding: "10px 18px",
                background: "transparent",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = C.accent;
                e.currentTarget.style.color = C.accent;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = C.border;
                e.currentTarget.style.color = C.inkSoft;
              }}
            >
              {copied ? (
                <>
                  <Check size={13} /> Copied
                </>
              ) : (
                <>
                  <Share2 size={13} /> Share This Quote
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

/* -------------------------------------------------------- Problem ------ */
const PAINS = [
  {
    n: "01",
    title: "You look fine on paper.",
    body:
      "The job. The relationship. The life you were supposed to build. Everyone else nods. You smile. Inside, something is missing.",
  },
  {
    n: "02",
    title: "You know it's not the real you.",
    body:
      "There is a self underneath the version you perform every day — and the gap between them is becoming impossible to ignore.",
  },
  {
    n: "03",
    title: "Drifting feels safer than choosing.",
    body:
      "Another year passes. Another decision postponed. The comfort zone keeps whispering it knows what's best.",
  },
  {
    n: "04",
    title: "You're tired of starting over.",
    body:
      "You've read the books, listened to the podcasts, journaled for a week. Nothing held. Because nothing went deep enough.",
  },
];

function ProblemLight() {
  return (
    <section
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-16"
      data-testid="light-problem-section"
      style={{ background: C.bg, borderTop: `1px solid ${C.border}` }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-5 md:sticky md:top-32 self-start">
            <Overline>The unspoken truth</Overline>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 1.2 }}
              style={{
                ...SPECTRAL,
                fontWeight: 700,
                color: C.ink,
                fontSize: "clamp(36px, 5vw, 72px)",
                lineHeight: 1,
                letterSpacing: "-0.02em",
              }}
            >
              Functioning
              <br />
              on the outside.
              <br />
              <span style={{ fontStyle: "italic", color: C.accent }}>
                Hollow
              </span>{" "}
              on the inside.
            </motion.h2>
            <p
              className="max-w-md"
              style={{
                ...SPECTRAL,
                fontSize: 18,
                lineHeight: 1.7,
                color: C.inkSoft,
                marginTop: 32,
              }}
            >
              You are not broken. You are about to break{" "}
              <em style={{ fontStyle: "italic", color: C.ink }}>open</em>. That
              is the doorway.
            </p>
          </div>

          <div className="md:col-span-7 space-y-12 md:space-y-16">
            {PAINS.map((p, i) => (
              <motion.div
                key={p.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-15%" }}
                transition={{ duration: 1, delay: i * 0.05 }}
                data-testid={`light-pain-point-${p.n}`}
                style={{
                  paddingLeft: 48,
                  borderLeft: `1px solid ${C.border}`,
                  transition: "border-color 700ms",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.borderLeftColor = C.accent)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.borderLeftColor = C.border)
                }
              >
                <div
                  style={{
                    ...SPECTRAL,
                    fontSize: 11,
                    letterSpacing: "0.25em",
                    textTransform: "uppercase",
                    color: C.inkMuted,
                    marginBottom: 16,
                  }}
                >
                  {p.n} / 04
                </div>
                <h3
                  style={{
                    ...SPECTRAL,
                    fontWeight: 400,
                    color: C.ink,
                    fontSize: "clamp(26px, 3vw, 38px)",
                    lineHeight: 1.1,
                    letterSpacing: "-0.01em",
                    marginBottom: 16,
                  }}
                >
                  {p.title}
                </h3>
                <p
                  className="max-w-xl"
                  style={{
                    ...SPECTRAL,
                    fontSize: 17,
                    lineHeight: 1.7,
                    color: C.inkSoft,
                  }}
                >
                  {p.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- Solution ----- */
function SolutionLight() {
  return (
    <section
      id="guide"
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-16"
      data-testid="light-solution-section"
      style={{ background: C.bg }}
    >
      <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 md:gap-16 items-start">
        <div className="md:col-span-5">
          <Overline>The Guide</Overline>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2 }}
            style={{
              ...SPECTRAL,
              fontWeight: 700,
              color: C.ink,
              fontSize: "clamp(48px, 6vw, 96px)",
              lineHeight: 1,
              letterSpacing: "-0.02em",
            }}
          >
            The Dark
            <br />
            Night of the
            <br />
            <span style={{ fontStyle: "italic", color: C.accent }}>Soul.</span>
          </motion.h2>
          <div
            className="mt-10 inline-flex items-center gap-3"
            style={{
              ...SPECTRAL,
              fontSize: 11,
              letterSpacing: "0.25em",
              textTransform: "uppercase",
              color: C.inkSoft,
            }}
          >
            <span>A 6-Phase Transformation</span>
            <ArrowDownRight size={14} style={{ color: C.accent }} />
          </div>
        </div>

        <div className="md:col-span-7 md:pt-6 space-y-8">
          <p
            style={{
              ...SPECTRAL,
              fontWeight: 400,
              color: C.ink,
              fontSize: "clamp(24px, 3vw, 34px)",
              lineHeight: 1.25,
            }}
          >
            This is not a course. It is a passage.
          </p>
          <p
            className="max-w-2xl"
            style={{
              ...SPECTRAL,
              fontSize: 18,
              lineHeight: 1.7,
              color: C.inkSoft,
            }}
          >
            A six-phase digital guide built on lived experience and grounded in
            the neuroscience of identity change.{" "}
            <span style={{ color: C.ink }}>35 deep questions</span> that will
            not let you lie to yourself.{" "}
            <span style={{ color: C.ink }}>6 practical tools</span> you can use
            the same day. And the unflinching framework for how the old self
            dies — and the real you finally walks out.
          </p>
          <p
            className="max-w-2xl"
            style={{
              ...SPECTRAL,
              fontSize: 18,
              lineHeight: 1.7,
              color: C.inkSoft,
            }}
          >
            You don't walk around the dark. You walk through it. And on the
            other side, you stop performing a life that was never yours.
          </p>
          <div
            className="mt-10 grid grid-cols-3 gap-6 pt-8"
            style={{ borderTop: `1px solid ${C.border}` }}
          >
            {[
              { k: "6", v: "Phases" },
              { k: "35", v: "Questions" },
              { k: "6", v: "Tools" },
            ].map((s) => (
              <div key={s.v}>
                <div
                  style={{
                    ...SPECTRAL,
                    fontSize: 48,
                    fontWeight: 400,
                    color: C.ink,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {s.k}
                </div>
                <div
                  style={{
                    ...SPECTRAL,
                    fontSize: 10,
                    letterSpacing: "0.25em",
                    textTransform: "uppercase",
                    color: C.inkMuted,
                    marginTop: 4,
                  }}
                >
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- What's Inside */
const PHASES = [
  { n: "01", title: "The Crack", body: "The first honest admission: something is wrong. You stop pretending the dissatisfaction will pass on its own." },
  { n: "02", title: "The Descent", body: "You let the old story collapse. Not in chaos — in clarity. You stop defending the version of you that no longer fits." },
  { n: "03", title: "The Void", body: "The hardest phase. Old identity gone, new one not yet formed. Most people run. You learn to stay." },
  { n: "04", title: "The Reckoning", body: "Deep questions you have spent your life avoiding. The 35 are designed to bypass your performance and reach what's underneath. Including a deep chapter on finding your true core — who you are beneath everything you were taught to be." },
  { n: "05", title: "The Rebuild", body: "Six practical tools. Daily mechanics for thinking, deciding, and acting from the real self — not the one you inherited." },
  { n: "06", title: "The Emergence", body: "Confidence as residue, not requirement. You become the proof of your own work." },
];

function InsideLight() {
  return (
    <section
      id="inside"
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-16"
      data-testid="light-whats-inside-section"
      style={{ background: C.bg, borderTop: `1px solid ${C.border}` }}
    >
      <div className="max-w-7xl mx-auto">
        <Overline>What's Inside</Overline>
        <h2
          className="max-w-4xl"
          style={{
            ...SPECTRAL,
            fontWeight: 700,
            color: C.ink,
            fontSize: "clamp(36px, 5vw, 72px)",
            lineHeight: 1,
            letterSpacing: "-0.02em",
          }}
        >
          Six phases. Thirty-five questions.{" "}
          <span style={{ fontStyle: "italic", color: C.accent }}>One self</span>{" "}
          to find.
        </h2>

        <div
          className="mt-20 grid md:grid-cols-2 lg:grid-cols-3"
          style={{ gap: 1, background: C.border, border: `1px solid ${C.border}` }}
        >
          {PHASES.map((p, i) => (
            <motion.div
              key={p.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.8, delay: i * 0.05 }}
              data-testid={`light-phase-${p.n}`}
              className="p-8 md:p-12 transition-colors duration-500"
              style={{ background: C.bgSoft }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = C.bgWarm)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = C.bgSoft)
              }
            >
              <div className="flex items-baseline justify-between mb-8">
                <span
                  style={{
                    ...SPECTRAL,
                    fontSize: 56,
                    fontWeight: 400,
                    color: C.accent,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {p.n}
                </span>
                <span
                  style={{
                    ...SPECTRAL,
                    fontSize: 10,
                    letterSpacing: "0.3em",
                    textTransform: "uppercase",
                    color: C.inkMuted,
                  }}
                >
                  Phase
                </span>
              </div>
              <h3
                style={{
                  ...SPECTRAL,
                  fontWeight: 500,
                  color: C.ink,
                  fontSize: "clamp(22px, 2.6vw, 30px)",
                  lineHeight: 1.1,
                  letterSpacing: "-0.01em",
                  marginBottom: 16,
                }}
              >
                {p.title}
              </h3>
              <p
                style={{
                  ...SPECTRAL,
                  fontSize: 16,
                  lineHeight: 1.7,
                  color: C.inkSoft,
                }}
              >
                {p.body}
              </p>
            </motion.div>
          ))}
        </div>

        <div
          className="mt-32 grid md:grid-cols-12 gap-10 pt-16"
          style={{ borderTop: `1px solid ${C.border}` }}
        >
          <div className="md:col-span-4">
            <div
              style={{
                ...BODY,
                fontSize: 11,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                color: C.coral,
              }}
            >
              Plus · The Science
            </div>
            <h3
              style={{
                ...SPECTRAL,
                fontWeight: 400,
                color: C.ink,
                fontSize: "clamp(28px, 3.5vw, 44px)",
                lineHeight: 1.05,
                letterSpacing: "-0.01em",
                marginTop: 16,
              }}
            >
              The neuroscience of becoming someone new.
            </h3>
          </div>
          <div className="md:col-span-8">
            <p
              className="max-w-2xl"
              style={{
                ...SPECTRAL,
                fontSize: 18,
                lineHeight: 1.7,
                color: C.inkSoft,
              }}
            >
              A full explanation of why identity change feels like dying —
              because, neurologically, it is. You will understand how
              predictive models in the brain protect the old self, why
              discomfort is a signal of rewiring (not failure), and exactly how
              to use it. This is the chapter most guides never give you.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- WhoFor ------- */
function WhoForLight() {
  const FOR = [
    "You are functioning on the outside, but hollow on the inside.",
    "You suspect you've been living someone else's life — maybe for years.",
    "Surface-level self-help has stopped working. You're done with shortcuts.",
    "You're willing to do the deep work, not just read about it.",
    "You're ready for the version of you that has been waiting underneath.",
  ];
  const NOT = [
    "You're looking for motivational quotes.",
    "You want to feel better without changing anything.",
    "You believe transformation should be comfortable.",
  ];
  return (
    <section
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-16"
      data-testid="light-who-its-for-section"
      style={{ background: C.bg, borderTop: `1px solid ${C.border}` }}
    >
      <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <Overline>Who it's for</Overline>
          <h2
            style={{
              ...SPECTRAL,
              fontWeight: 700,
              color: C.ink,
              fontSize: "clamp(36px, 4.5vw, 64px)",
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
            }}
          >
            This is for the ones{" "}
            <span style={{ fontStyle: "italic", color: C.accent }}>ready</span>{" "}
            to stop pretending.
          </h2>
        </div>
        <div className="md:col-span-7 space-y-12">
          <div>
            <div
              style={{
                ...BODY,
                fontSize: 10,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                color: C.coral,
                marginBottom: 24,
              }}
            >
              · For you if
            </div>
            <ul className="space-y-5">
              {FOR.map((l, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span
                    style={{
                      ...SPECTRAL,
                      fontSize: 10,
                      letterSpacing: "0.2em",
                      color: C.accent,
                      paddingTop: 8,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span
                    style={{
                      ...SPECTRAL,
                      fontSize: 21,
                      fontWeight: 400,
                      lineHeight: 1.3,
                      color: C.ink,
                    }}
                  >
                    {l}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div
            className="pt-10"
            style={{ borderTop: `1px solid ${C.border}` }}
          >
            <div
              style={{
                ...SPECTRAL,
                fontSize: 10,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                color: C.inkMuted,
                marginBottom: 24,
              }}
            >
              · Not for you if
            </div>
            <ul className="space-y-3">
              {NOT.map((l, i) => (
                <li
                  key={i}
                  style={{
                    ...SPECTRAL,
                    fontSize: 16,
                    color: C.inkMuted,
                    textDecoration: "line-through",
                    textDecorationColor: C.border,
                  }}
                >
                  {l}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- About Robin -- */
function AboutLight() {
  return (
    <section
      id="story"
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-16"
      data-testid="light-about-robin-section"
      style={{ background: C.bgSoft, borderTop: `1px solid ${C.border}` }}
    >
      <div className="max-w-5xl mx-auto">
        <Overline>My Story</Overline>
        <h2
          style={{
            ...SPECTRAL,
            fontWeight: 700,
            color: C.ink,
            fontSize: "clamp(48px, 6vw, 88px)",
            lineHeight: 1,
            letterSpacing: "-0.02em",
          }}
        >
          At <span style={{ fontStyle: "italic", color: C.accent }}>33</span>,
          I lost everything.
        </h2>

        <div
          className="mt-12 grid md:grid-cols-2 gap-8 pt-8 pb-8"
          style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}
        >
          <div>
            <div
              style={{
                ...SPECTRAL,
                fontSize: 36,
                fontWeight: 700,
                color: C.ink,
                letterSpacing: "-0.02em",
              }}
            >
              154K
            </div>
            <div
              style={{
                ...BODY,
                fontSize: 11,
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                color: C.inkMuted,
                marginTop: 4,
              }}
            >
              Instagram Followers
            </div>
          </div>
          <div>
            <div
              style={{
                ...SPECTRAL,
                fontSize: 36,
                fontWeight: 700,
                color: C.ink,
                letterSpacing: "-0.02em",
              }}
            >
              Millions
            </div>
            <div
              style={{
                ...BODY,
                fontSize: 11,
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                color: C.inkMuted,
                marginTop: 4,
              }}
            >
              Of Views
            </div>
          </div>
        </div>

        <div
          className="mt-12 space-y-6 max-w-2xl"
          style={{
            ...BODY,
            fontSize: 18,
            lineHeight: 1.7,
            color: C.ink,
          }}
        >
          <p>
            Business. Money. My best friend. Within a span that felt like a
            single long night, the entire structure I had built my identity
            on collapsed.
          </p>
          <p>
            I spent years after that drifting. Functioning, technically.
            Hollow, actually. I tried every shortcut. None of them held.
            Because none of them touched what was actually broken — the
            version of me that had been performing a life that was never mine
            to begin with.
          </p>
          <p>
            The only thing that worked was descent. Sitting with the dark
            instead of running from it. Asking questions that hurt. Building
            tools that didn't lie to me.
          </p>
          <p style={{ color: C.ink, fontWeight: 500 }}>
            Years later, I built this guide for the version of me that needed
            it back then. And for you, if you are where I was.
          </p>
        </div>

        <blockquote
          className="mt-12 max-w-3xl"
          style={{
            ...SPECTRAL,
            fontStyle: "italic",
            fontWeight: 400,
            fontSize: "clamp(24px, 3vw, 36px)",
            lineHeight: 1.3,
            color: C.ink,
            borderLeft: `2px solid ${C.accent}`,
            paddingLeft: 28,
          }}
        >
          "Built by someone who lost everything at 33 — and had to find his
          way back from scratch."
        </blockquote>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- Testimonials  */
const TESTIMONIALS = [
  { name: "Marcus L.", role: "Founder · 38", quote: "I'd been carrying a weight for almost a decade. Phase 3 cracked something open I couldn't have done alone. First time I felt like myself in years." },
  { name: "Sofia R.", role: "Designer · 31", quote: "Not another self-help PDF. This made me uncomfortable in exactly the way I needed. The questions stayed with me for weeks." },
  { name: "Aaron K.", role: "Surgeon · 42", quote: "I've read everything. Nothing prepared me for the neuroscience chapter. It reframed identity change in a way that finally clicked." },
  { name: "Lina M.", role: "Teacher · 29", quote: "Robin writes like someone who has actually been there. No motivational fluff. Just the work, and a hand to walk you through it." },
];

function TestimonialsLight() {
  return (
    <section
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-16"
      data-testid="light-testimonials-section"
      style={{ background: C.bg, borderTop: `1px solid ${C.border}` }}
    >
      <div className="max-w-7xl mx-auto">
        <Overline>Voices From The Other Side</Overline>
        <h2
          className="max-w-3xl"
          style={{
            ...SPECTRAL,
            fontWeight: 700,
            color: C.ink,
            fontSize: "clamp(36px, 4.5vw, 64px)",
            lineHeight: 1.05,
            letterSpacing: "-0.02em",
          }}
        >
          They walked through.{" "}
          <span style={{ fontStyle: "italic", color: C.accent }}>
            Here's what came back.
          </span>
        </h2>

        <div
          className="mt-16 grid md:grid-cols-2"
          style={{ gap: 1, background: C.border, border: `1px solid ${C.border}` }}
        >
          {TESTIMONIALS.map((t, i) => (
            <motion.figure
              key={t.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 1, delay: i * 0.07 }}
              data-testid={`light-testimonial-${i}`}
              className="p-8 md:p-12 transition-colors duration-500"
              style={{ background: C.bgSoft }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = C.bgWarm)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = C.bgSoft)
              }
            >
              <blockquote
                style={{
                  ...SPECTRAL,
                  fontSize: "clamp(20px, 2.2vw, 26px)",
                  fontWeight: 400,
                  lineHeight: 1.35,
                  color: C.ink,
                }}
              >
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption
                className="mt-8 flex items-center gap-4 pt-6"
                style={{ borderTop: `1px solid ${C.border}` }}
              >
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: 40,
                    height: 40,
                    background: C.accent,
                    color: C.bg,
                    ...SPECTRAL,
                    fontSize: 18,
                    fontWeight: 500,
                  }}
                >
                  {t.name.charAt(0)}
                </div>
                <div>
                  <div
                    style={{
                      ...SPECTRAL,
                      fontSize: 14,
                      color: C.ink,
                    }}
                  >
                    {t.name}
                  </div>
                  <div
                    style={{
                      ...SPECTRAL,
                      fontSize: 10,
                      letterSpacing: "0.2em",
                      textTransform: "uppercase",
                      color: C.inkMuted,
                    }}
                  >
                    {t.role}
                  </div>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- Pricing ------ */
const INCLUDES = [
  "Full 6-phase digital guide (PDF)",
  "35 deep questions designed to bypass performance",
  "6 practical tools you can use the same day",
  "Neuroscience-based explanation of identity change",
  "Lifetime access, on every device",
  "All future updates, included",
];

function PricingLight() {
  const [email, setEmail] = useState("");
  const { loading, handle } = useBuy();

  return (
    <section
      id="pricing"
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-16 overflow-hidden"
      data-testid="light-pricing-section"
      style={{ background: C.bg, borderTop: `1px solid ${C.border}` }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% 0%, rgba(45,85,68,0.06) 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-4 mb-8">
            <span style={{ height: 1, width: 40, background: C.coral }} />
            <span
              style={{
                ...BODY,
                fontSize: 11,
                letterSpacing: "0.35em",
                textTransform: "uppercase",
                color: C.coral,
              }}
            >
              The Investment
            </span>
            <span style={{ height: 1, width: 40, background: C.coral }} />
          </div>
          <h2
            style={{
              ...SPECTRAL,
              fontWeight: 700,
              color: C.ink,
              fontSize: "clamp(36px, 4.5vw, 64px)",
              lineHeight: 1,
              letterSpacing: "-0.02em",
            }}
          >
            One decision.{" "}
            <span style={{ fontStyle: "italic", color: C.accent }}>
              One life.
            </span>
          </h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2 }}
          data-testid="light-pricing-card"
          className="p-8 md:p-16"
          style={{
            background: C.bgSoft,
            border: `1px solid ${C.border}`,
          }}
        >
          <div className="grid md:grid-cols-12 gap-10 md:gap-14">
            <div className="md:col-span-5">
              <div
                style={{
                  ...BODY,
                  fontSize: 11,
                  letterSpacing: "0.3em",
                  textTransform: "uppercase",
                  color: C.coral,
                  marginBottom: 16,
                }}
              >
                Launch Price
              </div>
              <div className="flex items-baseline gap-4">
                <span
                  data-testid="light-launch-price"
                  style={{
                    ...SPECTRAL,
                    fontSize: "clamp(72px, 10vw, 128px)",
                    fontWeight: 700,
                    color: C.ink,
                    letterSpacing: "-0.04em",
                    lineHeight: 1,
                  }}
                >
                  47€
                </span>
                <span
                  data-testid="light-regular-price"
                  style={{
                    ...SPECTRAL,
                    fontSize: 28,
                    color: C.inkMuted,
                    textDecoration: "line-through",
                  }}
                >
                  97€
                </span>
              </div>
              <div
                style={{
                  ...SPECTRAL,
                  fontSize: 11,
                  letterSpacing: "0.25em",
                  textTransform: "uppercase",
                  color: C.inkSoft,
                  marginTop: 12,
                }}
              >
                One-time · Lifetime Access
              </div>

              <div className="mt-8">
                <label
                  htmlFor="light-purchase-email"
                  style={{
                    ...SPECTRAL,
                    fontSize: 10,
                    letterSpacing: "0.25em",
                    textTransform: "uppercase",
                    color: C.inkMuted,
                    display: "block",
                    marginBottom: 12,
                  }}
                >
                  · Email for guide delivery (required)
                </label>
                <input
                  id="light-purchase-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@somewhere.real"
                  data-testid="light-purchase-email-input"
                  className="w-full transition-colors"
                  style={{
                    ...SPECTRAL,
                    fontSize: 16,
                    background: "transparent",
                    border: `1px solid ${C.border}`,
                    color: C.ink,
                    padding: "12px 16px",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = C.accent)}
                  onBlur={(e) => (e.target.style.borderColor = C.border)}
                />
              </div>

              <button
                onClick={() => {
                  const trimmed = email.trim();
                  trackGA("cta_click", { cta_location: "pricing", cta_label: "Begin the Descent · 47€" });
                  if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
                    trackGA("cta_click_no_email", { reason: "invalid_email" });
                    toast.error("Please enter a valid email so we can send you the guide.");
                    document.getElementById("light-purchase-email")?.focus();
                    return;
                  }
                  handle(trimmed);
                }}
                disabled={loading}
                data-testid="light-pricing-buy-button"
                className="mt-6 w-full inline-flex items-center justify-center gap-4 transition-colors duration-300 disabled:opacity-60"
                style={{
                  background: C.accent,
                  color: C.bg,
                  padding: "20px 32px",
                }}
                onMouseEnter={(e) => {
                  if (!loading)
                    e.currentTarget.style.background = C.accentHover;
                }}
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = C.accent)
                }
              >
                <span
                  style={{
                    ...SPECTRAL,
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: "0.3em",
                    textTransform: "uppercase",
                  }}
                >
                  {loading ? "Connecting…" : "Begin the Descent"}
                </span>
              </button>

              <div
                className="mt-8 flex items-start gap-4 pt-6"
                style={{ borderTop: `1px solid ${C.border}` }}
                data-testid="light-guarantee-block"
              >
                <ShieldCheck
                  style={{ color: C.accent, flexShrink: 0, marginTop: 4 }}
                  size={20}
                />
                <div>
                  <div
                    style={{
                      ...SPECTRAL,
                      fontSize: 17,
                      fontWeight: 500,
                      color: C.ink,
                    }}
                  >
                    30-Day Promise
                  </div>
                  <p
                    style={{
                      ...SPECTRAL,
                      fontSize: 14,
                      lineHeight: 1.6,
                      color: C.inkSoft,
                      marginTop: 4,
                    }}
                  >
                    If you do the work in good faith and feel nothing changed,
                    write us within 30 days. Full refund. No questions, no
                    friction.
                  </p>
                </div>
              </div>
            </div>

            <div
              className="md:col-span-7 md:pl-12"
              style={{ borderLeft: `1px solid ${C.border}` }}
            >
              <div
                style={{
                  ...SPECTRAL,
                  fontSize: 11,
                  letterSpacing: "0.3em",
                  textTransform: "uppercase",
                  color: C.inkSoft,
                  marginBottom: 24,
                }}
              >
                · What's Included
              </div>
              <ul className="space-y-4">
                {INCLUDES.map((line, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-4 py-2"
                    style={{
                      borderBottom: `1px solid ${C.borderSoft}`,
                    }}
                  >
                    <Check
                      style={{ color: C.accent, flexShrink: 0, marginTop: 4 }}
                      size={18}
                    />
                    <span
                      style={{
                        ...SPECTRAL,
                        fontSize: 16,
                        lineHeight: 1.6,
                        color: C.ink,
                      }}
                    >
                      {line}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- FAQ ---------- */
const FAQS = [
  { q: "I've tried self-help before. Why will this be different?", a: "Most self-help works on the surface — habits, mindset, motivation. This guide works at the level of identity, using a six-phase neuroscience-based framework that explains why old patterns hold and exactly how to dissolve them. It is not designed to make you feel better. It is designed to make you become someone real." },
  { q: "How long does it take to go through?", a: "Self-paced. Some people move through it in two weeks. Most take 30–60 days to do it right. There's no rush. You have lifetime access — return as many times as the descent asks." },
  { q: "Do I get a refund if it's not for me?", a: "Yes. Do the work in good faith. If within 30 days you feel nothing shifted, write us and we refund you fully. No questions, no friction." },
  { q: "Is this therapy?", a: "No. This is a structured transformation guide built from lived experience and grounded in neuroscience. It is not a substitute for therapy or clinical care. If you're in crisis, please seek a qualified professional first." },
  { q: "What format is the guide in?", a: "Digital. PDF. Optimized for phone, tablet, and desktop. You can begin within minutes of purchase." },
  { q: "Why is the price so low right now?", a: "47€ is the launch price for the founding cohort. Once the launch window closes, it returns to the full 97€ permanently." },
];

function FaqLight() {
  return (
    <section
      id="faq"
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-16"
      data-testid="light-faq-section"
      style={{ background: C.bg, borderTop: `1px solid ${C.border}` }}
    >
      <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <Overline>FAQ</Overline>
          <h2
            style={{
              ...SPECTRAL,
              fontWeight: 700,
              color: C.ink,
              fontSize: "clamp(36px, 4vw, 56px)",
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
            }}
          >
            Questions,{" "}
            <span style={{ fontStyle: "italic", color: C.accent }}>
              honestly answered.
            </span>
          </h2>
        </div>
        <div className="md:col-span-8">
          <Accordion
            type="single"
            collapsible
            className="w-full"
            data-testid="light-faq-accordion"
          >
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                style={{ borderBottom: `1px solid ${C.border}` }}
                data-testid={`light-faq-item-${i}`}
              >
                <AccordionTrigger
                  className="hover:no-underline"
                  style={{
                    ...SPECTRAL,
                    fontWeight: 500,
                    color: C.ink,
                    fontSize: "clamp(18px, 2vw, 24px)",
                    lineHeight: 1.3,
                    textAlign: "left",
                    padding: "24px 0",
                  }}
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent
                  style={{
                    ...SPECTRAL,
                    fontSize: 16,
                    lineHeight: 1.7,
                    color: C.inkSoft,
                    padding: "8px 0 24px 0",
                  }}
                >
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- Final CTA --- */
function FinalCtaLight() {
  return (
    <section
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-16 overflow-hidden"
      data-testid="light-final-cta-section"
      style={{ background: C.bgSoft, borderTop: `1px solid ${C.border}` }}
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(45,85,68,0.05) 0%, transparent 70%)",
        }}
      />
      <div className="relative max-w-5xl mx-auto text-center">
        <div className="inline-flex items-center gap-4 mb-10">
          <span style={{ height: 1, width: 40, background: C.coral }} />
          <span
            style={{
              ...BODY,
              fontSize: 11,
              letterSpacing: "0.35em",
              textTransform: "uppercase",
              color: C.coral,
            }}
          >
            The doorway
          </span>
          <span style={{ height: 1, width: 40, background: C.coral }} />
        </div>
        <h2
          style={{
            ...SPECTRAL,
            fontWeight: 700,
            color: C.ink,
            fontSize: "clamp(48px, 6vw, 96px)",
            lineHeight: 1,
            letterSpacing: "-0.02em",
          }}
        >
          The old self must{" "}
          <span style={{ fontStyle: "italic", color: C.accent }}>die</span>
          <br />
          for the real you to emerge.
        </h2>
        <p
          className="max-w-2xl mx-auto"
          style={{
            ...SPECTRAL,
            fontSize: 18,
            lineHeight: 1.7,
            color: C.inkSoft,
            marginTop: 40,
          }}
        >
          You've read enough. Felt enough. The next move isn't another
          article. It's a decision. Walk through the darkness — not around it.
        </p>
        <button
          onClick={() => {
            trackGA("cta_click", { cta_location: "final_cta", cta_label: "Begin the Descent · 47€" });
            document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
          }}
          data-testid="light-final-cta-button"
          className="mt-12 inline-flex items-center justify-center gap-4 transition-colors duration-300"
          style={{ background: C.accent, color: C.bg, padding: "20px 40px" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = C.accentHover)}
          onMouseLeave={(e) => (e.currentTarget.style.background = C.accent)}
        >
          <span
            style={{
              ...SPECTRAL,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.3em",
              textTransform: "uppercase",
            }}
          >
            {"Begin the Descent · 47€"}
          </span>
        </button>
        <div
          style={{
            ...SPECTRAL,
            fontSize: 11,
            letterSpacing: "0.25em",
            textTransform: "uppercase",
            color: C.inkMuted,
            marginTop: 24,
          }}
        >
          <span style={{ textDecoration: "line-through", marginRight: 12 }}>
            97€
          </span>
          Launch price · Limited
        </div>
      </div>

      <footer
        className="relative mt-32 md:mt-48 pt-12 max-w-7xl mx-auto"
        style={{ borderTop: `1px solid ${C.border}` }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div
            style={{
              ...SPECTRAL,
              fontSize: 22,
              color: C.ink,
            }}
          >
            Robin{" "}
            <span style={{ fontStyle: "italic", color: C.accent }}>
              Weyersberg
            </span>
          </div>
          <div className="flex items-center gap-6">
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: C.inkSoft }}
              data-testid="light-instagram-link"
              aria-label="Instagram"
            >
              <Instagram size={18} />
            </a>
            <a
              href="#faq"
              style={{
                ...SPECTRAL,
                fontSize: 11,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: C.inkSoft,
              }}
            >
              FAQ
            </a>
          </div>
        </div>
        <div
          className="mt-10 pb-10 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
          style={{
            ...SPECTRAL,
            fontSize: 10,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: C.inkMuted,
          }}
        >
          <span>
            © {new Date().getFullYear()} Robin Weyersberg. All rights
            reserved.
          </span>
          <span>The Dark Night of the Soul · A Six-Phase Guide</span>
        </div>
      </footer>
    </section>
  );
}

/* -------------------------------------------------------- Reframe ------ */
function ReframeLight() {
  return (
    <section
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-16"
      data-testid="light-reframe-section"
      style={{ background: C.bgSoft, borderTop: `1px solid ${C.border}` }}
    >
      <div className="max-w-4xl mx-auto">
        <Overline>A word before you go further</Overline>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          style={{
            ...SPECTRAL,
            fontWeight: 700,
            color: C.ink,
            fontSize: "clamp(40px, 6vw, 88px)",
            lineHeight: 1,
            letterSpacing: "-0.02em",
          }}
        >
          It is not happening
          <br />
          <span style={{ color: C.inkMuted }}>to</span> you.
          <br />
          It is happening{" "}
          <span style={{ fontStyle: "italic", color: C.accent }}>
            for
          </span>{" "}
          you.
        </motion.h2>

        <div
          className="mt-14 space-y-6 max-w-2xl"
          style={{
            ...BODY,
            fontSize: 18,
            lineHeight: 1.7,
            color: C.ink,
          }}
        >
          <p>
            The dark night is not an accident. It is not punishment. It is the
            soul demanding to be heard.
          </p>
          <p>
            Every wisdom tradition in human history has recognized this
            moment — the day the life lived on the surface stops being enough,
            and something deeper begins to push through.
          </p>
          <p style={{ fontWeight: 500 }}>
            What feels like collapse is, in fact, an invitation. The only
            question is whether you answer it.
          </p>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- Universal --- */
function UniversalLight() {
  return (
    <section
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-16"
      data-testid="light-universal-section"
      style={{ background: C.bgSoft, borderTop: `1px solid ${C.border}` }}
    >
      <div className="max-w-4xl mx-auto">
        <Overline>The oldest story ever told</Overline>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          style={{
            ...SPECTRAL,
            fontWeight: 700,
            color: C.ink,
            fontSize: "clamp(40px, 6vw, 88px)",
            lineHeight: 1,
            letterSpacing: "-0.02em",
          }}
        >
          You are not
          <br />
          <span style={{ fontStyle: "italic", color: C.accent }}>broken.</span>
          <br />
          You are in the
          <br />
          <span style={{ color: C.inkMuted }}>oldest</span> transformation
          story ever told.
        </motion.h2>

        <div
          className="mt-14 space-y-6 max-w-2xl"
          style={{ ...BODY, fontSize: 18, lineHeight: 1.7, color: C.ink }}
        >
          <p>
            The Dark Night of the Soul is not a modern concept. Jung called it{" "}
            <em style={{ fontStyle: "italic", color: C.accent }}>
              Individuation
            </em>
            . Campbell called it the{" "}
            <em style={{ fontStyle: "italic", color: C.accent }}>
              Hero's Journey
            </em>
            . The mystics called it the soul's passage through darkness.
          </p>
          <p>
            Every wisdom tradition in human history has recognized this
            moment — when everything false finally collapses and what is real
            can finally emerge.
          </p>
          <p style={{ fontWeight: 500 }}>
            You are not broken. You are in the oldest transformation story
            ever told.
          </p>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- Unconscious -- */
function UnconsciousLight() {
  return (
    <section
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-16"
      data-testid="light-unconscious-section"
      style={{ background: C.bg, borderTop: `1px solid ${C.border}` }}
    >
      <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 md:gap-16 items-start">
        <div className="md:col-span-5 md:sticky md:top-32 self-start">
          <Overline>The invisible engine</Overline>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 1.2 }}
            style={{
              ...SPECTRAL,
              fontWeight: 700,
              color: C.ink,
              fontSize: "clamp(36px, 5vw, 72px)",
              lineHeight: 1,
              letterSpacing: "-0.02em",
            }}
          >
            <span style={{ fontStyle: "italic", color: C.accent }}>95%</span>
            <br />
            of your life
            <br />
            is running
            <br />
            in the dark.
          </motion.h2>
          <div
            className="mt-10"
            style={{
              ...SPECTRAL,
              fontSize: 11,
              letterSpacing: "0.25em",
              textTransform: "uppercase",
              color: C.inkSoft,
            }}
          >
            <span>The Unconscious Mind</span>
          </div>
        </div>

        <div
          className="md:col-span-7 md:pt-6 space-y-8 max-w-2xl"
          style={{ ...SPECTRAL, fontSize: 18, lineHeight: 1.7, color: C.inkSoft }}
        >
          <p>
            Most people don't realize that{" "}
            <span style={{ color: C.ink }}>
              95% of their decisions, reactions, and behaviors
            </span>{" "}
            come from the unconscious mind — not from conscious choice.
          </p>
          <p>
            The patterns that are holding you back, the self-sabotage that
            keeps repeating, the life that feels stuck — these are not
            character flaws. They are{" "}
            <span style={{ color: C.ink }}>
              unconscious programs running silently in the background
            </span>
            , installed long before you had the awareness to question them.
          </p>
          <p style={{ color: C.ink, fontWeight: 500 }}>
            This guide makes the invisible visible.
          </p>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- Relationships */
function RelationshipsLight() {
  return (
    <section
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-16"
      data-testid="light-relationships-section"
      style={{ background: C.bg, borderTop: `1px solid ${C.border}` }}
    >
      <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 md:gap-16 items-start">
        <div className="md:col-span-5">
          <Overline>Who stays. Who leaves.</Overline>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 1.2 }}
            style={{
              ...SPECTRAL,
              fontWeight: 700,
              color: C.ink,
              fontSize: "clamp(36px, 5vw, 72px)",
              lineHeight: 1,
              letterSpacing: "-0.02em",
            }}
          >
            The darkness
            <br />
            <span style={{ color: C.inkMuted }}>shows you</span>
            <br />
            who is{" "}
            <span style={{ fontStyle: "italic", color: C.accent }}>
              really
            </span>{" "}
            there.
          </motion.h2>
        </div>

        <div
          className="md:col-span-7 md:pt-6 space-y-8 max-w-2xl"
          style={{ ...SPECTRAL, fontSize: 18, lineHeight: 1.7, color: C.inkSoft }}
        >
          <p>
            The Dark Night reveals your relational patterns with unusual
            clarity. Who shows up for you in the darkness — and who
            disappears — is information.
          </p>
          <p>
            But more importantly, it reveals{" "}
            <span style={{ color: C.ink }}>how you ask for help</span>, how
            much you allow yourself to be seen, and which relationships were
            built on who you truly are versus who you were performing to be.
          </p>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- Forgiveness -- */
function ForgivenessLight() {
  return (
    <section
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-16"
      data-testid="light-forgiveness-section"
      style={{ background: C.bgSoft, borderTop: `1px solid ${C.border}` }}
    >
      <div className="max-w-4xl mx-auto">
        <Overline>The final threshold</Overline>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          style={{
            ...SPECTRAL,
            fontWeight: 700,
            color: C.ink,
            fontSize: "clamp(40px, 6vw, 88px)",
            lineHeight: 1,
            letterSpacing: "-0.02em",
          }}
        >
          The last weight
          <br />
          to set down is
          <br />
          <span style={{ fontStyle: "italic", color: C.accent }}>
            self-forgiveness.
          </span>
        </motion.h2>

        <div
          className="mt-14 space-y-6 max-w-2xl"
          style={{ ...BODY, fontSize: 18, lineHeight: 1.7, color: C.ink }}
        >
          <p>
            The final block between where you are and who you are becoming is
            self-forgiveness.
          </p>
          <p>
            The years spent lost, the patterns that didn't serve you, the time
            it took to get here — none of it was wasted. You did the best you
            could with the awareness you had.
          </p>
          <p style={{ fontWeight: 500 }}>
            This guide helps you release that weight — so the person you are
            becoming can finally move forward without it.
          </p>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------- Page --------- */
export default function LightLanding() {
  return (
    <main
      className="min-h-screen overflow-x-hidden theme-light"
      data-testid="light-landing-page"
      style={{ background: C.bg, color: C.ink, ...SPECTRAL }}
    >
      <NavLight />
      <HeaderBanner />
      <HeroLight />
      <QuoteLight
        testId="light-quote-break-1"
        slug="comfort-zone"
        quote="The comfort zone is not safe. It is where the self goes to"
        italic="slowly disappear."
      />
      <ProblemLight />
      <UnconsciousLight />
      <ReframeLight />
      <SolutionLight />
      <InsideLight />
      <QuoteLight
        testId="light-quote-break-2"
        slug="old-self-must-die"
        quote="The old self must die for the real you to"
        italic="emerge."
      />
      <WhoForLight />
      <RelationshipsLight />
      <AboutLight />
      <QuoteLight
        testId="light-quote-break-3"
        slug="self-confidence"
        quote="Self-confidence is not a prerequisite for action. It is the"
        italic="result of it."
      />
      <PricingLight />
      <FaqLight />
      <FinalCtaLight />
      <ForgivenessLight />
      <Toaster theme="light" position="top-center" />
    </main>
  );
}
